/**
 * Utilitaire de journalisation pour gérer les logs conditionnellement selon l'environnement
 * Utilise import.meta.env.DEV pour n'afficher les logs qu'en développement
 */

/**
 * Log d'information (niveau info)
 * @param message Message principal
 * @param data Données additionnelles à logger
 */
export function logInfo(message: string, data?: unknown): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
}

/**
 * Log d'avertissement (niveau warning)
 * @param message Message d'avertissement
 * @param data Données additionnelles à logger
 */
export function logWarning(message: string, data?: unknown): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.warn(message, data);
    } else {
      console.warn(message);
    }
  }
}

/**
 * Log d'erreur (niveau error)
 * @param message Message d'erreur
 * @param error Objet d'erreur ou données additionnelles
 */
export function logError(message: string, error?: unknown): void {
  if (import.meta.env.DEV) {
    if (error !== undefined) {
      console.error(message, error);
    } else {
      console.error(message);
    }
  }
}

/**
 * Log de débogage avancé (pour les développeurs)
 * @param message Message de débogage
 * @param data Données additionnelles à logger
 */
export function logDebug(message: string, data?: unknown): void {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.debug(message, data);
    } else {
      console.debug(message);
    }
  }
}

/**
 * Utilitaire complet de journalisation
 */
export const logger = {
  info: logInfo,
  warn: logWarning,
  error: logError,
  debug: logDebug
};

/**
 * Hook pour utiliser le logger avec un préfixe de composant
 * @param componentName Nom du composant qui utilise le logger
 * @returns Un objet logger avec des méthodes préfixées
 */
export function useLogger(componentName: string) {
  return {
    info: (message: string, data?: unknown) => logInfo(`[${componentName}] ${message}`, data),
    warn: (message: string, data?: unknown) => logWarning(`[${componentName}] ${message}`, data),
    error: (message: string, error?: unknown) => logError(`[${componentName}] ${message}`, error),
    debug: (message: string, data?: unknown) => logDebug(`[${componentName}] ${message}`, data)
  };
}

export default logger;
